import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useSavingsRateTrend } from "../hooks/cashflow";
import type { SavingsRatePoint } from "../lib/cashflow";

type RangeKey = "6M" | "12M" | "24M" | "60M";
const RANGES: { key: RangeKey; months: number }[] = [
  { key: "6M", months: 6 },
  { key: "12M", months: 12 },
  { key: "24M", months: 24 },
  { key: "60M", months: 60 },
];

function formatPercent(bp: number | null): string {
  if (bp === null) return "—";
  return `${(bp / 100).toFixed(1)} %`;
}

function formatEur(cents: number): string {
  return (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

interface ChartPoint {
  month: string;
  planned: number | null;
  actual: number | null;
}

export function CashflowSavingsRate() {
  const { t } = useTranslation();
  const [range, setRange] = useState<RangeKey>("12M");
  const months = RANGES.find((r) => r.key === range)?.months ?? 12;
  const trend = useSavingsRateTrend(months);

  const data = trend.data?.points ?? [];
  const chartData = useMemo<ChartPoint[]>(
    () =>
      data.map((p) => ({
        month: p.month,
        planned: p.plannedRateBp === null ? null : p.plannedRateBp / 100,
        actual: p.actualRateBp === null ? null : p.actualRateBp / 100,
      })),
    [data],
  );

  const currentPoint: SavingsRatePoint | undefined = data[data.length - 1];
  const compareIndex = data.length - 4;
  const comparePoint: SavingsRatePoint | undefined =
    compareIndex >= 0 ? data[compareIndex] : undefined;
  const deltaBp =
    currentPoint?.actualRateBp !== null &&
    currentPoint?.actualRateBp !== undefined &&
    comparePoint?.actualRateBp !== null &&
    comparePoint?.actualRateBp !== undefined
      ? currentPoint.actualRateBp - comparePoint.actualRateBp
      : null;

  const yearAggregates = useMemo(() => {
    const byYear = new Map<
      string,
      { income: number; expense: number; actualIncome: number; actualExpense: number }
    >();
    for (const p of data) {
      const y = p.month.slice(0, 4);
      const cur = byYear.get(y) ?? { income: 0, expense: 0, actualIncome: 0, actualExpense: 0 };
      cur.income += p.plannedIncomeCents;
      cur.expense += p.plannedExpenseCents;
      cur.actualIncome += p.actualIncomeCents;
      cur.actualExpense += p.actualExpenseCents;
      byYear.set(y, cur);
    }
    return Array.from(byYear.entries()).map(([year, agg]) => ({
      year,
      plannedRateBp:
        agg.income > 0 ? Math.round(((agg.income - agg.expense) / agg.income) * 10_000) : null,
      actualRateBp:
        agg.actualIncome > 0
          ? Math.round(((agg.actualIncome - agg.actualExpense) / agg.actualIncome) * 10_000)
          : null,
      plannedIncome: agg.income,
      plannedExpense: agg.expense,
      actualIncome: agg.actualIncome,
      actualExpense: agg.actualExpense,
    }));
  }, [data]);

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div
            className="text-xs font-semibold uppercase tracking-wider mb-1"
            style={{ color: "var(--muted)" }}
          >
            {t("nav.group_cashflow")}
          </div>
          <h1 className="text-2xl font-bold tracking-tight">{t("cashflow.savings.title")}</h1>
        </div>
        <div
          className="flex gap-0.5 p-0.5 rounded-lg"
          style={{ background: "var(--card)", border: "1px solid var(--border)" }}
        >
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => setRange(r.key)}
              className="px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
              style={
                range === r.key
                  ? { background: "var(--text)", color: "var(--bg)" }
                  : { color: "var(--muted)" }
              }
            >
              {r.key}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="fv-card p-5">
          <div className="fv-kpi-label mb-1">
            {t("cashflow.savings.kpi.current_actual")}
          </div>
          <div className="text-2xl font-bold tracking-tight num">
            {formatPercent(currentPoint?.actualRateBp ?? null)}
          </div>
          <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>
            {currentPoint
              ? t("cashflow.savings.kpi.current_month", { month: currentPoint.month })
              : ""}
          </div>
        </div>
        <div className="fv-card p-5">
          <div className="fv-kpi-label mb-1">
            {t("cashflow.savings.kpi.current_planned")}
          </div>
          <div
            className="text-2xl font-bold tracking-tight num"
            style={{ color: "var(--muted)" }}
          >
            {formatPercent(currentPoint?.plannedRateBp ?? null)}
          </div>
          <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>
            {t("cashflow.savings.kpi.planned_hint")}
          </div>
        </div>
        <div className="fv-card p-5">
          <div className="fv-kpi-label mb-1">{t("cashflow.savings.kpi.delta_3m")}</div>
          <div
            className="text-2xl font-bold tracking-tight num"
            style={{
              color:
                deltaBp === null
                  ? "var(--muted)"
                  : deltaBp >= 0
                    ? "var(--pos)"
                    : "var(--neg)",
            }}
          >
            {deltaBp === null
              ? "—"
              : `${deltaBp > 0 ? "+" : ""}${(deltaBp / 100).toFixed(1)} pp`}
          </div>
          <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>
            {comparePoint
              ? t("cashflow.savings.kpi.vs", { month: comparePoint.month })
              : t("cashflow.savings.kpi.delta_unavailable")}
          </div>
        </div>
      </div>

      <div className="fv-card p-5">
        <h2 className="text-base font-semibold mb-3">
          {t("cashflow.savings.chart.title")}
        </h2>
        <div style={{ width: "100%", height: 320 }}>
          <ResponsiveContainer>
            <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
              <CartesianGrid stroke="var(--grid)" strokeDasharray="3 3" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 11, fill: "var(--muted)" }}
                minTickGap={24}
              />
              <YAxis
                tickFormatter={(v: number) => `${v.toFixed(0)} %`}
                tick={{ fontSize: 11, fill: "var(--muted)" }}
                width={56}
              />
              <Tooltip
                formatter={(v: number) => `${v.toFixed(1)} %`}
                labelFormatter={(l: string) => l}
                contentStyle={{
                  background: "var(--card)",
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  color: "var(--text)",
                }}
              />
              <ReferenceLine y={0} stroke="var(--subtle)" strokeDasharray="3 3" />
              <Legend wrapperStyle={{ fontSize: 12, color: "var(--muted)" }} />
              <Line
                type="monotone"
                dataKey="planned"
                name={t("cashflow.savings.chart.planned")}
                stroke="var(--muted)"
                strokeDasharray="4 4"
                strokeWidth={1.5}
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="actual"
                name={t("cashflow.savings.chart.actual")}
                stroke="var(--accent)"
                strokeWidth={2}
                dot={{ r: 3 }}
                connectNulls
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {yearAggregates.length > 1 && (
        <div className="fv-card overflow-hidden">
          <div
            className="px-5 py-3 border-b"
            style={{ borderColor: "var(--border)" }}
          >
            <h2 className="text-base font-semibold">
              {t("cashflow.savings.yearly.title")}
            </h2>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr
                className="text-left text-xs"
                style={{
                  background: "var(--border-soft)",
                  color: "var(--muted)",
                }}
              >
                <th className="px-3 py-2 font-semibold">
                  {t("cashflow.savings.yearly.year")}
                </th>
                <th className="px-3 py-2 text-right font-semibold">
                  {t("cashflow.savings.yearly.income")}
                </th>
                <th className="px-3 py-2 text-right font-semibold">
                  {t("cashflow.savings.yearly.expense")}
                </th>
                <th className="px-3 py-2 text-right font-semibold">
                  {t("cashflow.savings.yearly.planned_rate")}
                </th>
                <th className="px-3 py-2 text-right font-semibold">
                  {t("cashflow.savings.yearly.actual_rate")}
                </th>
              </tr>
            </thead>
            <tbody>
              {yearAggregates.map((y) => (
                <tr
                  key={y.year}
                  className="border-b"
                  style={{ borderColor: "var(--border-soft)" }}
                >
                  <td className="px-3 py-2.5">{y.year}</td>
                  <td className="px-3 py-2.5 text-right num">
                    {formatEur(y.actualIncome)}
                  </td>
                  <td className="px-3 py-2.5 text-right num">
                    {formatEur(y.actualExpense)}
                  </td>
                  <td
                    className="px-3 py-2.5 text-right num"
                    style={{ color: "var(--muted)" }}
                  >
                    {formatPercent(y.plannedRateBp)}
                  </td>
                  <td className="px-3 py-2.5 text-right num">
                    {formatPercent(y.actualRateBp)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
